import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, Trash2, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import HeaderUtils from "@/components/HeaderUtils";
import BrandLockup from "@/components/BrandLockup";
import PrimaryNav from "@/components/PrimaryNav";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const STARTERS = [
  "Am I eating enough carbs for my hardest session this week?",
  "What should I eat before an early morning long run?",
  "My protein keeps falling short — what do I change?",
  "How should I fuel race week?",
  "Why am I always starving in the evenings?",
];

// AI Fuelling Coach — Claude Sonnet 4.6, given the athlete's own plan,
// targets, training week, logged food and eating times server-side.
export default function AskCoach() {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const autoSent = useRef(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/ai/coach/history").then(({ data }) => {
      setSessionId(data.session_id || null);
      setMessages(data.messages || []);
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  // Deep link from elsewhere in the app: /ask-ai?q=... auto-asks once.
  // Wait for history to load first (otherwise the history response can
  // overwrite the optimistic user bubble), skip if that exact question is
  // already the last thing asked, and strip ?q= so a refresh can't re-fire.
  useEffect(() => {
    if (!loaded || autoSent.current) return;
    autoSent.current = true;
    const q = new URLSearchParams(window.location.search).get("q");
    if (!q) return;
    window.history.replaceState({}, "", "/ask-ai");
    const asked = [...messages].reverse().find((m) => m.role === "user");
    if (asked && asked.content === q) return;
    send(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/coach", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The coach is busy — try again");
      setMessages((m) => m.slice(0, -1));
      setInput(msg);
    } finally { setBusy(false); }
  };

  const clear = async () => {
    try {
      await api.delete(`/ai/coach/history${sessionId ? `?session_id=${sessionId}` : ""}`);
      setMessages([]); setSessionId(null);
      toast.success("Conversation cleared");
    } catch { toast.error("Could not clear"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <BrandLockup />
          <div className="ml-auto shrink-0"><HeaderUtils /></div>
        </div>
        <PrimaryNav />
      </header>

      <main data-testid="ask-coach-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="grid place-items-center h-11 w-11 bg-[#A78BFA] shrink-0"><Sparkles className="h-5 w-5 text-black" /></div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-none">Fuelling Coach</h1>
            <p className="text-sm text-zinc-300 mt-1.5">Ask anything about your fuelling. It can see your plan, targets, training week, logged food and eating times — so answers use your real numbers.</p>
          </div>
          {messages.length > 0 && (
            <button data-testid="coach-clear-btn" onClick={clear} title="Clear conversation"
              className="ml-auto shrink-0 p-2 border border-white/10 text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/40 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {messages.length === 0 && (
          <div className="border border-dashed border-white/15 p-5 mb-5" data-testid="coach-starters">
            <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400 mb-3">Try one of these</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {STARTERS.map((s, i) => (
                <button key={s} data-testid={`coach-starter-${i}`} onClick={() => send(s)} disabled={busy}
                  className="text-left border border-white/10 hover:border-[#A78BFA]/60 hover:bg-[#A78BFA]/[0.06] px-3 py-2.5 text-sm text-zinc-200 transition-colors disabled:opacity-50">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3 mb-4" data-testid="coach-thread">
          {messages.map((m, i) => (
            <div key={i} data-testid={`coach-msg-${m.role}-${i}`}
              className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="grid place-items-center h-8 w-8 shrink-0 border border-[#A78BFA]/40 bg-[#A78BFA]/10">
                  <Bot className="h-4 w-4 text-[#A78BFA]" />
                </div>
              )}
              <div className={`max-w-[46rem] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#E2E30A] text-black font-medium"
                  : "border border-white/10 bg-[#141414] text-zinc-100"}`}>
                {/* Models occasionally emit **bold** — strip it so it doesn't
                    render as literal asterisks. */}
                {String(m.content || "").replace(/\*\*/g, "")}
              </div>
              {m.role === "user" && (
                <div className="grid place-items-center h-8 w-8 shrink-0 border border-white/15 bg-white/5">
                  <UserIcon className="h-4 w-4 text-zinc-300" />
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex gap-3" data-testid="coach-thinking">
              <div className="grid place-items-center h-8 w-8 shrink-0 border border-[#A78BFA]/40 bg-[#A78BFA]/10">
                <Bot className="h-4 w-4 text-[#A78BFA]" />
              </div>
              <div className="border border-white/10 bg-[#141414] px-4 py-3 text-sm text-zinc-400 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-[#A78BFA]" /> Reading your week…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="sticky bottom-0 bg-[#0A0A0A] pt-3 pb-2 border-t border-white/10">
          <div className="flex gap-2">
            <textarea data-testid="coach-input" rows={1} value={input} disabled={busy}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about your fuelling…"
              className="flex-1 min-w-0 resize-none bg-[#141414] border border-white/15 focus:border-[#A78BFA] outline-none px-3 py-3 text-sm text-white placeholder:text-zinc-500" />
            <Button data-testid="coach-send-btn" onClick={() => send()} disabled={busy || !input.trim()}
              className="rounded-none h-auto px-4 bg-[#A78BFA] hover:bg-[#8f6ff5] text-black font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">Guidance only — not medical advice. Speak to your GP or a registered dietitian about clinical concerns.</p>
        </div>
      </main>
    </div>
  );
}
